import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Chip,
  Button,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Stack,
  Rating,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Avatar,
  Alert,
  IconButton,
  Tooltip,
  Badge,
  Collapse
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  Inventory as InventoryIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalOffer as LocalOfferIcon,
  Category as CategoryIcon,
  Store as StoreIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Share as ShareIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteIcon,
  Image as ImageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";

const ProductSinglePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const singleProduct = useSelector((state) => state.products.singleProduct);
  
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [showAllAttributes, setShowAllAttributes] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showFullAddress, setShowFullAddress] = useState(false);
  const [descriptionNeedsTruncation, setDescriptionNeedsTruncation] = useState(false);
  const [addressNeedsTruncation, setAddressNeedsTruncation] = useState(false);
  
  const descriptionRef = useRef(null);
  const addressRef = useRef(null);

  console.log("singleProduct2520", singleProduct);

  // Get current selected variant with null checks
  const currentVariant = singleProduct?.variants?.[selectedVariantIndex] || {};
  
  // Get images from the selected variant with null checks
  const ProductImages = currentVariant?.media?.map(m => m?.media_url).filter(Boolean) || 
                         singleProduct?.media?.map(m => m?.media_url).filter(Boolean) || [];

  // Get MRP price from selected variant with null checks
  const MrpPrice = parseFloat(currentVariant?.selling_price || singleProduct?.min_price || "0");
  const OriginalPrice = parseFloat(currentVariant?.mrp_price || singleProduct?.max_price || "0");

  // Get attributes directly from singleProduct with null checks
  const attributes = singleProduct?.attributes || [];

  // Get specifications directly with null checks
  const specifications = singleProduct?.specifications || [];

  // Get categories name with null checks
  const categoryName = singleProduct?.categories?.[0]?.name || "N/A";
  const allCategories = singleProduct?.categories || [];

  // Get brand name from brand object with null checks
  const brandName = singleProduct?.brand?.name || "No Brand";

  // Helper function to get address string
  const getAddressString = () => {
    if (!singleProduct?.merchant?.address) return '';
    const address = singleProduct.merchant.address;
    const addressParts = [
      address.address,
      address.landmark,
      `${address.city || ''}${address.city && address.state ? ', ' : ''}${address.state || ''}`,
      `${address.country || ''}${address.country && address.pincode ? ' - ' : ''}${address.pincode || ''}`
    ].filter(part => part && part.trim());
    
    return addressParts.join('\n');
  };
  
  // Define fullAddress BEFORE it's used in useEffect
  const fullAddress = getAddressString();

  useEffect(() => {
    setSelectedImage(0);
  }, [selectedVariantIndex]);

  useEffect(() => {
    if (ProductImages?.length > 1) {
      const interval = setInterval(() => {
        setSelectedImage((prev) => (prev + 1) % ProductImages?.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [ProductImages?.length]);

  // Check if content needs truncation based on height or line count
  useEffect(() => {
    if (descriptionRef.current) {
      const lineHeight = parseInt(getComputedStyle(descriptionRef.current).lineHeight);
      const maxHeight = lineHeight * 3; // 3 lines
      const contentHeight = descriptionRef.current.scrollHeight;
      setDescriptionNeedsTruncation(contentHeight > maxHeight);
    }
  }, [singleProduct?.description]);

  useEffect(() => {
    if (addressRef.current && fullAddress) {
      const lineHeight = parseInt(getComputedStyle(addressRef.current).lineHeight);
      const maxHeight = lineHeight * 3; // 3 lines
      const contentHeight = addressRef.current.scrollHeight;
      setAddressNeedsTruncation(contentHeight > maxHeight);
    }
  }, [fullAddress]);

  const nextImage = () => {
    if (ProductImages?.length) {
      setSelectedImage((prev) => (prev + 1) % ProductImages.length);
    }
  };

  const prevImage = () => {
    if (ProductImages?.length) {
      setSelectedImage((prev) => (prev - 1 + ProductImages.length) % ProductImages.length);
    }
  };

  // Format price
  const formatPrice = (price) => {
    if (!price || isNaN(price)) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Calculate discount percentage
  const calculateDiscount = (mrp, selling) => {
    if (!mrp || !selling) return null;
    const discount = ((mrp - selling) / mrp) * 100;
    return Math.round(discount);
  };

  const discountPercentage = OriginalPrice && MrpPrice && OriginalPrice > MrpPrice
    ? Math.round(((OriginalPrice - MrpPrice) / OriginalPrice) * 100)
    : 0;

  const handleEditProduct = () => {
    navigate(`/products/edit/${singleProduct?.id}`);
  };

  const handleDeleteProduct = () => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      console.log('Delete product:', singleProduct?.id);
    }
  };

  if (!singleProduct) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6">Loading product details...</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Grid container spacing={3}>
        {/* Left Column - Image Gallery */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: 'sticky', top: 24 }}>
            {/* Main Image */}
            <Paper 
              sx={{ 
                position: 'relative',
                p: 3,
                mb: 2,
                height: { xs: 300, md: 400, lg: 500 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f8fafc',
                borderRadius: 2,
                overflow: 'hidden'
              }}
              elevation={0}
              variant="outlined"
            >
              {ProductImages?.length > 0 ? (
                <img
                  src={ProductImages[selectedImage]}
                  alt={currentVariant?.sku_code || singleProduct?.title || 'Product image'}
                  style={{
                    maxWidth: '85%',
                    maxHeight: '85%',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  <ImageIcon sx={{ fontSize: 64, mb: 1 }} />
                  <Typography>No image available</Typography>
                </Box>
              )}

              {/* Navigation Arrows */}
              {ProductImages?.length > 1 && (
                <>
                  <IconButton
                    onClick={prevImage}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'background.paper' }
                    }}
                  >
                    ‹
                  </IconButton>
                  <IconButton
                    onClick={nextImage}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'background.paper',
                      boxShadow: 2,
                      '&:hover': { bgcolor: 'background.paper' }
                    }}
                  >
                    ›
                  </IconButton>
                </>
              )}

              {/* Share Button */}
              <IconButton
                onClick={() => setIsFavorite(!isFavorite)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  color: isFavorite ? 'error.main' : 'text.secondary',
                  '&:hover': { bgcolor: 'background.paper' }
                }}
              >
                {isFavorite ? <FavoriteIcon /> : <ShareIcon />}
              </IconButton>

              {/* Image Indicator Dots */}
              {ProductImages?.length > 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 1
                  }}
                >
                  {ProductImages?.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: index === selectedImage ? 'primary.main' : 'grey.400',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'scale(1.2)' }
                      }}
                    />
                  ))}
                </Box>
              )}
            </Paper>

            {/* Thumbnail Strip */}
            {ProductImages?.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                {ProductImages?.map((image, index) => (
                  <Box
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    sx={{
                      flexShrink: 0,
                      width: 80,
                      height: 80,
                      border: '2px solid',
                      borderColor: index === selectedImage ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      bgcolor: '#f8fafc',
                      '&:hover': { borderColor: 'primary.light' }
                    }}
                  >
                    <img
                      src={image}
                      alt={`Product view ${index + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Grid>

        {/* Right Column - Content */}
        <Grid item xs={12} md={7}>
          {/* Header Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
              {singleProduct?.title || 'Product Title'}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {currentVariant?.sku_code && (
                <Chip
                  label={currentVariant?.sku_code}
                  variant="outlined"
                  size="small"
                />
              )}
              <Chip
                label={brandName}
                variant="outlined"
                size="small"
              />
              {singleProduct?.featured && (
                <Chip
                  label="Featured"
                  color="warning"
                  size="small"
                />
              )}
            </Box>
          </Box>

          {/* Pricing Card */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }} elevation={0} variant="outlined">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Price Details
              </Typography>
              {discountPercentage > 0 && (
                <Chip
                  label={`${discountPercentage}% OFF`}
                  color="success"
                  size="small"
                  icon={<LocalOfferIcon />}
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatPrice(MrpPrice)}
              </Typography>
              {OriginalPrice > MrpPrice && OriginalPrice > 0 && (
                <>
                  <Typography variant="body1" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                    {formatPrice(OriginalPrice)}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    Save {formatPrice(OriginalPrice - MrpPrice)}
                  </Typography>
                </>
              )}
            </Box>
          </Paper>

          {/* Variants Section */}
          {singleProduct?.variants?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Available Variants
              </Typography>
              <Grid container spacing={2}>
                {singleProduct?.variants?.map((variant, index) => {
                  const variantMrpPrice = parseFloat(variant?.selling_price || "0");
                  const isSelected = index === selectedVariantIndex;
                  const variantDiscount = calculateDiscount(variant?.mrp_price, variant?.selling_price);
                  
                  return (
                    <Grid item xs={6} sm={4} md={3} key={variant?.id || index}>
                      <Card
                        onClick={() => setSelectedVariantIndex(index)}
                        sx={{
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          bgcolor: isSelected ? 'action.hover' : 'background.paper',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: 'primary.light',
                            transform: 'translateY(-2px)',
                            boxShadow: 2
                          },
                          position: 'relative'
                        }}
                        elevation={0}
                      >
                        <CardContent sx={{ p: 2, textAlign: 'center' }}>
                          {variant?.media?.[0]?.media_url ? (
                            <Box sx={{ height: 100, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <img
                                src={variant?.media?.[0]?.media_url}
                                alt={variant?.sku_code || 'Variant image'}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              />
                            </Box>
                          ) : (
                            <Box sx={{ height: 100, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', borderRadius: 1 }}>
                              <ImageIcon sx={{ color: 'text.disabled' }} />
                            </Box>
                          )}
                          
                          <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                            {variant?.sku_code || 'N/A'}
                          </Typography>
                          
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mt: 0.5 }}>
                            {formatPrice(variantMrpPrice)}
                          </Typography>
                          
                          <Typography variant="caption" color="text.secondary">
                            Stock: {variant?.stock_quantity || 0}
                          </Typography>

                          {variant?.is_default && (
                            <Badge
                              badgeContent="Default"
                              color="primary"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                '& .MuiBadge-badge': { fontSize: '0.65rem', height: 20 }
                              }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, py: 1.5 }}
            >
              Buy Now
            </Button>
            <Button
              variant="contained"
              size="large"
              fullWidth
              sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, py: 1.5 }}
            >
              Add to Cart
            </Button>
          </Box>

          {/* Product Details Grid */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, borderRadius: 2 }} elevation={0} variant="outlined">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CategoryIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {categoryName}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2, borderRadius: 2 }} elevation={0} variant="outlined">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <InventoryIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary">
                    Available Stock
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {currentVariant?.stock_quantity || singleProduct?.total_stock || 0} units
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Specifications Section */}
          {specifications?.length > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }} elevation={0} variant="outlined">
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Specifications
              </Typography>
              <Grid container spacing={2}>
                {specifications?.map((spec) => (
                  <Grid item xs={12} sm={6} key={spec?.id || Math.random()}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {spec?.name || 'N/A'}
                      </Typography>
                      <Typography variant="body1">
                        {spec?.value || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Description with Show More/Less using Collapse */}
          {singleProduct?.description && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, }} elevation={0} variant="outlined">
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Description
              </Typography>
              <Box>
                <Collapse in={showFullDescription} collapsedSize={80}>
                  <Typography 
                    ref={descriptionRef}
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      height: showFullDescription ? 'auto' : 80,
                    }}
                  >
                    {singleProduct.description}
                  </Typography>
                </Collapse>
                {(descriptionNeedsTruncation || singleProduct.description.length > 3) && (
                  <Button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    endIcon={showFullDescription ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mt: 1, textTransform: 'none' }}
                    size="small"
                  >
                    {showFullDescription ? 'Show Less' : 'Show More'}
                  </Button>
                )}
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Seller Information - Full Width */}
        {singleProduct?.merchant && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 2 }} elevation={0} variant="outlined">
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Seller Information
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {/* Logo */}
                {singleProduct?.merchant?.logo ? (
                  <Box sx={{ flexShrink: 0 }}>
                    <img
                      src={singleProduct?.merchant?.logo}
                      alt={`${singleProduct?.merchant?.name || 'Seller'} logo`}
                      style={{
                        width: 80,
                        height: 80,
                        objectFit: 'contain',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0'
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/80?text=Logo';
                      }}
                    />
                  </Box>
                ) : (
                  <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                    <StoreIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                )}
                
                {/* Company Info */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {singleProduct?.merchant?.name || "Company name not available"}
                  </Typography>
                  
                  {singleProduct?.merchant?.domain && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">Website:</Typography>
                      <Link
                        href={`https://${singleProduct?.merchant?.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ fontSize: '0.875rem' }}
                      >
                        {singleProduct?.merchant?.domain}
                      </Link>
                    </Box>
                  )}
                  
                  {singleProduct?.merchant?.email && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Email: {singleProduct?.merchant?.email}
                    </Typography>
                  )}
                  
                  {singleProduct?.merchant?.phone && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Phone: {singleProduct?.merchant?.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
              
              {/* Address with Show More/Less using Collapse */}
              {singleProduct?.merchant?.address && (
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                    Business Address
                  </Typography>
                  <Box>
                    <Collapse in={showFullAddress} collapsedSize={80}>
                      <Typography 
                        ref={addressRef}
                        variant="body2" 
                        color="text.secondary"
                        sx={{  lineHeight: 1.5,whiteSpace: 'pre-wrap', height: showFullAddress ? 'auto' : 80 }}
                      >
                        {fullAddress}
                      </Typography>
                    </Collapse>
                    {(addressNeedsTruncation || fullAddress?.length > 2) && (
                      <Button
                        onClick={() => setShowFullAddress(!showFullAddress)}
                        endIcon={showFullAddress ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{  textTransform: 'none' }}
                        size="small"
                      >
                        {showFullAddress ? 'Show Less' : 'Show More'}
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default ProductSinglePage;